import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiLinkedin, FiMessageSquare, FiMap, FiBookOpen, FiHelpCircle } from 'react-icons/fi';
import './Benefits.css'; // Import the CSS file

const Benefits = () => {
  const benefitsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    benefitsRef.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: <FiFileText />,
      title: 'Professional Resume Building',
      description: 'Get a professionally crafted ATS-friendly resume optimized for your career goals',
      color: '#000000'
    },
    {
      icon: <FiLinkedin />,
      title: 'LinkedIn Profile Makeover',
      description: 'Complete LinkedIn optimization with professional headline, summary, and content',
      color: '#1a1a1a'
    },
    {
      icon: <FiMessageSquare />,
      title: 'Interview Preparation',
      description: 'Mock interviews with feedback and common interview question preparation',
      color: '#2d2d2d'
    },
    {
      icon: <FiMap />,
      title: 'Career Roadmap',
      description: 'Personalized career roadmap for the next 1-3 years based on your stream',
      color: '#000000'
    },
    {
      icon: <FiBookOpen />,
      title: 'Exclusive Learning Materials',
      description: 'Access to premium study materials, templates, and industry resources',
      color: '#1a1a1a'
    },
    {
      icon: <FiHelpCircle />,
      title: 'Lifetime Support',
      description: 'Continued guidance and support even after program completion',
      color: '#2d2d2d'
    }
  ];

  const additionalBenefits = [
    'Certificate of Training Completion',
    'Certificate of Internship Completion',
    'Letter of Recommendation (Based on Performance)',
    'Industry Project Experience Certificate',
    'Access to Alumni Network',
    'Job Placement Assistance',
    'Freelance Opportunity Guidance',
    'Portfolio Building Support'
  ];

  return (
    <section id="benefits" className="benefits-section section-padding">
      <div className="benefits-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="benefits-header"
        >
          <h2 className="section-title">Additional Benefits</h2>
          <p className="section-subtitle">
            Get more than just training with our comprehensive career boost package
          </p>
        </motion.div>

        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              ref={(el) => (benefitsRef.current[index] = el)}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="benefit-card fade-in"
              style={{ '--benefit-color': benefit.color }} // Dynamic color via CSS variable
            >
              <div className="benefit-icon">
                {React.cloneElement(benefit.icon, {
                  size: 28,
                  color: '#ffffff'
                })}
              </div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-description">{benefit.description}</p>
              <div className="benefit-tag">FREE BONUS</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="additional-section"
        >
          <h3 className="additional-title">More Value Additions</h3>
          <div className="additional-grid">
            {additionalBenefits.map((benefit, index) => (
              <div key={index} className="additional-item">
                <div className="additional-check" />
                <span className="additional-text">{benefit}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="value-proposition"
        >
          <div className="value-card">
            <h3 className="value-title">Total Value: ₹10,000+</h3>
            <p className="value-text">
              Get all these benefits for just <strong>₹599</strong>
            </p>
            <div className="value-breakdown">
              <div className="value-item">
                <div className="value-amount">₹5,000</div>
                <div className="value-label">Training Value</div>
              </div>
              <div className="value-separator">+</div>
              <div className="value-item">
                <div className="value-amount">₹3,000</div>
                <div className="value-label">Internship Value</div>
              </div>
              <div className="value-separator">+</div>
              <div className="value-item">
                <div className="value-amount">₹2,000</div>
                <div className="value-label">Career Support</div>
              </div>
              <div className="value-separator">=</div>
              <div className="value-total">
                <div className="total-amount">₹599</div>
                <div className="total-label" style={{ color: 'white' }}>You Pay</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;